<template>
    <div class="modal fade" id="bulkOrderAssignModal" tabindex="-1" aria-labelledby="bulkOrderAssignModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div class="modal-dialog modal-xl">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="bulkOrderAssignModalLabel">Bulk Order Assign</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body p-0">
                    <ul class="nav nav-tabs px-3 pt-3 border-bottom-0" role="tablist">
                        <li class="nav-item">
                            <button class="nav-link" :class="{ active: activeTab === 'assign' }" @click="activeTab = 'assign'">Assign Orders</button>
                        </li>
                        <li class="nav-item">
                            <button class="nav-link" :class="{ active: activeTab === 'history' }" @click="activeTab = 'history'">Bulk History</button>
                        </li>
                    </ul>
                    
                    <div class="tab-content p-3 border-top">
                        <!-- Assign Tab -->
                        <div class="tab-pane fade" :class="{ 'show active': activeTab === 'assign' }">
                            <form @submit.prevent>
                                <div class="row g-3 mb-4">
                                    <!-- Filters -->
                                    <div class="col-md-12 mb-2" v-if="isEditMode">
                                        <div class="alert alert-info d-flex justify-content-between align-items-center">
                                            <span><i class="fas fa-edit me-2"></i>Editing Bulk Assignment #{{ editingAssignmentId }}</span>
                                            <button class="btn btn-sm btn-outline-info" @click="cancelEdit">Cancel Edit</button>
                                        </div>
                                    </div>
                                    <div class="col-md-6" style="position: relative; z-index: 1060;" :class="{'opacity-50 pe-none': isEditMode}">
                                        <label class="form-label">Select Services</label>
                                        <vue-multiselect
                                            v-model="selectedServices"
                                            :options="filteredServicesOptions"
                                            :multiple="true"
                                            :close-on-select="true"
                                            track-by="id"
                                            label="name"
                                            :internal-search="false"
                                            placeholder="Select Services"
                                            @search-change="onServiceSearchChange"
                                            @select="onServiceSelect"
                                            @update:modelValue="fetchPendingOrders"
                                        >
                                            <template #option="{ option }">
                                                <div v-if="option.is_special" :class="{'text-danger': option.id === 'remove_all', 'fw-bold': true}">
                                                    {{ option.name }}
                                                </div>
                                                <div v-else>
                                                    {{ option.name }}
                                                </div>
                                            </template>
                                        </vue-multiselect>
                                    </div>
                                    <div class="col-md-6" :class="{'opacity-50 pe-none': isEditMode}">
                                        <label class="form-label">Date Range</label>
                                        <VueDatePicker
                                            v-model="dateRange"
                                            range
                                            multi-calendars
                                            :enable-time-picker="false"
                                            :auto-apply="true"
                                            format="dd-MM-yyyy"
                                            @update:modelValue="fetchPendingOrders"
                                        ></VueDatePicker>
                                    </div>
                                </div>

                                <hr class="my-4">

                                <!-- Orders Table -->
                                <div class="row g-3">
                                    <div class="col-12">
                                        <div class="d-flex justify-content-between align-items-center mb-2">
                                            <h6 class="fw-bold">Available Orders ({{ orderList.length }})</h6>
                                            <span class="badge bg-primary fs-6">{{ selectedOrderIds.length }} Properties Selected</span>
                                        </div>
                                        
                                        <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                                            <table class="table table-bordered table-hover align-middle mb-0">
                                                <thead class="sticky-top custom-thead">
                                                    <tr>
                                                        <th class="text-center" width="50">
                                                            <input type="checkbox" class="form-check-input" v-model="selectAllOrders" @change="toggleSelectAll">
                                                        </th>
                                                        <th class="text-nowrap">Property</th>
                                                        <th class="text-nowrap">Service</th>
                                                        <th class="text-nowrap">Client</th>
                                                        <th class="text-nowrap text-end">Service Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="order in orderList" :key="order.id" :class="{'table-primary': selectedOrderIds.includes(order.id)}">
                                                        <td class="text-center">
                                                            <input type="checkbox" class="form-check-input" :value="order.id" v-model="selectedOrderIds">
                                                        </td>
                                                        <td>{{ order.property }}</td>
                                                        <td>{{ order.services }}</td>
                                                        <td>{{ order.client }}</td>
                                                        <td class="text-end">{{ formatDate(order.booking_date) }}</td>
                                                    </tr>
                                                    <tr v-if="isFetchingOrders">
                                                        <td colspan="5" class="text-center py-5">
                                                            <span class="spinner-border text-primary" role="status">
                                                                <span class="visually-hidden">Loading...</span>
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr v-else-if="orderList.length === 0">
                                                        <td colspan="5" class="text-center text-muted py-4">
                                                            {{ hasSearched ? 'No orders found for the selected criteria.' : 'Please select filters to view orders.' }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div v-if="errors.selectedOrders" class="text-danger small">{{ errors.selectedOrders }}</div>
                                    </div>
                                </div>

                                <hr class="my-4">

                                <!-- Assignment Details -->
                                <div class="row g-3">
                                    <div class="col-md-4">
                                        <label class="form-label">Service Hours <span class="text-danger">*</span></label>
                                        <vue-multiselect
                                            v-model="form.service_hours"
                                            :options="serviceHourOptions"
                                            placeholder="Select Hours"
                                            :searchable="false"
                                            :allow-empty="false"
                                            label="label"
                                            track-by="id"
                                            @select="fetchHandymen"
                                        ></vue-multiselect>
                                        <div v-if="errors.service_hours" class="text-danger small">{{ errors.service_hours }}</div>
                                    </div>

                                    <div class="col-md-4">
                                        <label class="form-label">Assign Date <span class="text-danger">*</span></label>
                                        <BookingDatePicker
                                            v-model="form.date"
                                            :format="'dd-MM-yyyy'"
                                            :time-picker="false"
                                            :auto-apply="true"
                                            :min-date="new Date()"
                                        />
                                        <div v-if="errors.date" class="text-danger small">{{ errors.date }}</div>
                                    </div>

                                    <div class="col-md-4">
                                        <label class="form-label">Assign Time <span class="text-danger">*</span></label>
                                        <TimePicker
                                            v-model="form.time"
                                            placeholder="Select Time"
                                            @update:modelValue="fetchHandymen"
                                        />
                                        <div v-if="errors.time" class="text-danger small">{{ errors.time }}</div>
                                    </div>

                                    <div class="col-12">
                                        <label class="form-label">Select Handyman <span class="text-danger">*</span></label>
                                        <vue-multiselect
                                            v-model="selectedHandymen"
                                            :options="filteredHandymans"
                                            :multiple="true"
                                            :close-on-select="true"
                                            label="name"
                                            :internal-search="false"
                                            track-by="id"
                                            placeholder="Select Handyman"
                                            :loading="isFetchingHandyman"
                                            @search-change="onHandymanSearchChange"
                                        >
                                            <template #option="{ option }">
                                                <div>
                                                    <div class="fw-bold">{{ option.name }}</div>
                                                    <div class="d-block small">{{ option.email }}</div>
                                                </div>
                                            </template>
                                        </vue-multiselect>
                                        <div v-if="errors.selectedHandymen" class="text-danger small">{{ errors.selectedHandymen }}</div>
                                        <small v-if="availableHandymans.length === 0 && !isFetchingHandyman && form.date && form.time" class="text-warning">
                                            No handymen available for the selected date and time.
                                        </small>
                                    </div>
                                </div>
                            </form>
                        </div>
                        
                        <!-- History Tab -->
                        <div class="tab-pane fade" :class="{ 'show active': activeTab === 'history' }">
                            <div class="d-flex justify-content-end mb-3">
                                <div style="width: 250px;">
                                    <VueDatePicker
                                        v-model="historyDate"
                                        :enable-time-picker="false"
                                        :auto-apply="true"
                                        placeholder="Filter by Date"
                                        format="dd-MM-yyyy"
                                        @update:modelValue="fetchHistory"
                                    ></VueDatePicker>
                                </div>
                            </div>
                            
                            <div class="table-responsive">
                                <table class="table table-bordered table-striped align-middle">
                                    <thead class="bg-light">
                                        <tr>
                                            <th>Assigned On</th>
                                            <th>Handyman</th>
                                            <th>Scheduled Date</th>
                                            <th>Orders</th>
                                            <th class="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="item in historyList" :key="item.id">
                                            <td>{{ formatDate(item.created_at) }}</td>
                                            <td>{{ item.handyman_names }}</td>
                                            <td>{{ formatDate(item.booking_date) }}</td>
                                            <td>{{ item.orders_count }}</td>
                                            <td class="text-end">
                                                <button class="btn btn-sm btn-outline-primary" @click="editAssignment(item)">
                                                    <i class="fas fa-edit"></i> Edit
                                                </button>
                                            </td>
                                        </tr>
                                        <tr v-if="isFetchingHistory">
                                            <td colspan="5" class="text-center py-4">
                                                <span class="spinner-border text-primary spinner-border-sm" role="status"></span> Loading...
                                            </td>
                                        </tr>
                                        <tr v-else-if="historyList.length === 0">
                                            <td colspan="5" class="text-center text-muted py-4">No history found.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" v-if="isEditMode" @click="cancelEdit">Cancel Edit</button>
                    <button type="button" class="btn btn-secondary" v-else data-bs-dismiss="modal">Close</button>
                    <button type="button" class="btn btn-theme" @click="submit" :disabled="isLoading">
                        <span v-if="isLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        {{ isEditMode ? 'Update Assignment' : 'Assign' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BookingDatePicker from '../BookingDatePicker.vue';
import TimePicker from '../partials/TimePicker.vue';
import moment from 'moment';
import { appMixin } from '../../mixin/appMixin';

export default {
    name: 'BulkOrderAssign',
    mixins: [appMixin],
    components: {
        BookingDatePicker,
        TimePicker,
    },
    data() {
        return {
            activeTab: 'assign',
            historyList: [],
            isFetchingHistory: false,
            historyDate: null,
            isEditMode: false,
            editingAssignmentId: null,

            // Filters
            services: [],
            selectedServices: [],
            dateRange: [],
            
            // Orders Data
            orderList: [],
            selectedOrderIds: [],
            hasSearched: false,
            selectAllOrders: false,

            // Assign Data
            selectedHandymen: [],
            availableHandymans: [],
            form: {
                service_hours: { id: 1, label: '1 Hr' },
                date: new Date(),
                time: { hours: 8, minutes: 0 },
            },
            
            // UI State
            serviceSearchQuery: '',
            handymanSearchQuery: '',
            serviceHourOptions: [
                { id: 1, label: '1 Hr' },
                { id: 2, label: '2 Hrs' },
                { id: 3, label: '3 Hrs' },
                { id: 4, label: '4 Hrs' },
                { id: 5, label: '5 Hrs' },
                { id: 6, label: '6 Hrs' },
                { id: 7, label: '7 Hrs' },
                { id: 8, label: '8 Hrs' },
            ],
            errors: {},
            isLoading: false,
            isFetchingOrders: false,
            isFetchingHandyman: false,
        };
    },
    watch: {
        'form.date': {
            handler(val) {
                if (val && this.errors.date) delete this.errors.date;
                this.fetchHandymen();
            },
            deep: true
        },
        'form.time': {
            handler(val) {
                if (val && this.errors.time) delete this.errors.time;
                this.fetchHandymen();
            },
            deep: true
        },
        'form.service_hours': {
            handler(val) {
                if (val && this.errors.service_hours) delete this.errors.service_hours;
            }
        },
        selectedOrderIds(newVal) {
             this.updateSelectAllState();
             if (newVal.length > 0 && this.errors.selectedOrders) {
                 delete this.errors.selectedOrders;
             }
        },
        selectedHandymen(newVal) {
            if (newVal.length > 0 && this.errors.selectedHandymen) {
                delete this.errors.selectedHandymen;
            }
        },
        activeTab(val) {
            if (val === 'history' && this.historyList.length === 0) {
                this.fetchHistory();
            }
        }
    },
    computed: {
        filteredServicesOptions() {
            const selectAll = { id: 'select_all', name: 'Select All', is_special: true };
            const removeSelected = { id: 'remove_all', name: 'Remove Selected', is_special: true };

            const baseOptions = [selectAll];
            if (this.selectedServices.length > 0) {
                baseOptions.push(removeSelected);
            }

            if (!this.serviceSearchQuery) {
                return [...baseOptions, ...this.services];
            }
            const query = this.serviceSearchQuery.toLowerCase().replace(/\s+/g, '');
            const filteredResults = this.services.filter(s => {
                const label = s.name.toLowerCase().replace(/\s+/g, '');
                return label.includes(query);
            });
            return [...baseOptions, ...filteredResults];
        },
        filteredHandymans() {
            if (!this.handymanSearchQuery) {
                return this.availableHandymans;
            }
            const query = this.handymanSearchQuery.toLowerCase().replace(/\s+/g, '');
            return this.availableHandymans.filter(handyman => {
                const label = `${handyman.name} ${handyman.email}`.toLowerCase().replace(/\s+/g, '');
                return label.includes(query);
            });
        }
    },
    mounted() {
        // Initialize Date Range (Next 60 days)
        const startDate = new Date();
        const endDate = new Date();
        endDate.setDate(startDate.getDate() + 60);
        this.dateRange = [startDate, endDate];

        this.fetchServices();
        this.fetchPendingOrders();
        
        // Initial fetch for handymen
        this.fetchHandymen();
    },
    methods: {
        async fetchServices() {
            try {
                const response = await axios.post('/api/admin/order-services'); // Assuming this returns services list properly or check structure
                // API AdminApiController::getOrderServices usually returns services list.
                // Should check if it returns {status, services} or just list.
                // Standard Laravel API response usually wraps in 'data' or specific key.
                // Assuming response.data.services or response.data.data
                this.services = response.data.services || response.data.data || [];
            } catch (error) {
                console.error("Error fetching services", error);
            }
        },

        async fetchPendingOrders() {
            if (this.isEditMode) return;
            
            this.hasSearched = true;
            this.isFetchingOrders = true;
            this.orderList = [];
            
            try {
                let starts = '';
                let ends = '';
                
                if (this.dateRange && this.dateRange[0] && this.dateRange[1]) {
                    starts = moment(this.dateRange[0]).format('YYYY-MM-DD');
                    ends = moment(this.dateRange[1]).format('YYYY-MM-DD');
                }

                // Filter out special options just in case
                const serviceIds = this.selectedServices
                    .filter(s => !s.is_special)
                    .map(s => s.id);

                const response = await axios.post('/api/admin/orders/bulk-assign-list', {
                    service_ids: serviceIds,
                    start_date: starts,
                    end_date: ends
                });

                if (response.data.status === 'success') {
                    this.orderList = response.data.orders;
                }
            } catch (error) {
                console.error("Error fetching orders", error);
            } finally {
                this.isFetchingOrders = false;
            }
        },

        onServiceSearchChange(query) {
            this.serviceSearchQuery = query;
        },

        onServiceSelect(option) {
            if (option.id === 'select_all') {
                const visibleRealServices = this.filteredServicesOptions.filter(s => !s.is_special);
                this.$nextTick(() => {
                    const existingMap = new Map(this.selectedServices.map(s => [s.id, s]));
                    visibleRealServices.forEach(s => {
                        if (!existingMap.has(s.id)) {
                            existingMap.set(s.id, s);
                        }
                    });
                    // Filter out any special options that entered via v-model
                    this.selectedServices = Array.from(existingMap.values()).filter(s => !s.is_special);
                    this.fetchPendingOrders();
                });
            } else if (option.id === 'remove_all') {
                this.$nextTick(() => {
                    this.selectedServices = [];
                    this.fetchPendingOrders();
                });
            } else {
                 // For normal selection, v-model handles it. 
                 // We just ensure special options didn't sneak in (though they shouldn't if not selected)
                 // But wait, if we select 'select_all', v-model adds it. This check runs before nextTick update?
                 // No, @select runs before v-model update usually.
            }
        },

        formatDate(date) {
            return date ? moment(date).format('DD MMM YYYY') : '';
        },

        toggleSelectAll() {
             if (this.selectAllOrders) {
                 this.selectedOrderIds = this.orderList.map(order => order.id);
             } else {
                 this.selectedOrderIds = [];
             }
        },

        updateSelectAllState() {
             if (this.orderList.length > 0 && this.selectedOrderIds.length === this.orderList.length) {
                 this.selectAllOrders = true;
             } else {
                 this.selectAllOrders = false;
             }
        },

        async fetchHandymen() {
            if (!this.form.date || !this.form.time) return;

            this.isFetchingHandyman = true;
            try {
                const serviceHours = parseFloat(this.form.service_hours?.id || 1);
                const startHour = parseInt(this.form.time.hours);
                const startMinute = parseInt(this.form.time.minutes);
                
                const endDateTime = moment()
                    .hour(startHour)
                    .minute(startMinute)
                    .add(serviceHours, 'hours');

                const payload = {
                    booking_date: moment(this.form.date).format('YYYY-MM-DD'),
                    start_time: this.form.time,
                    end_time: {
                        hours: endDateTime.hour(),
                        minutes: endDateTime.minute()
                    }
                };
                
                const users = await this.getHandymanForTaskAssignment(payload);
                this.availableHandymans = (users && users.length > 0) ? users : [];
            } catch (error) {
                console.error("Error fetching handymen", error);
                this.availableHandymans = [];
            } finally {
                this.isFetchingHandyman = false;
            }
        },

        onHandymanSearchChange(query) {
            this.handymanSearchQuery = query;
        },

        validate() {
            this.errors = {};
            let isValid = true;

            if (this.selectedOrderIds.length === 0) {
                this.errors.selectedOrders = "Please select at least one order from the list.";
                isValid = false;
            }
            if (!this.form.service_hours) {
                this.errors.service_hours = "Please enter service hours.";
                isValid = false;
            }
            if (!this.form.date) {
                this.errors.date = "Please select a date.";
                isValid = false;
            }
            if (!this.form.time) {
                this.errors.time = "Please select a time.";
                isValid = false;
            }
            if (this.selectedHandymen.length === 0) {
                this.errors.selectedHandymen = "Please select at least one handyman.";
                isValid = false;
            }

            return isValid;
        },

        async submit() {
            if (!this.validate()) return;

            this.isLoading = true;
            const handymanIds = this.selectedHandymen.map(h => h.id);

            const payload = {
                order_ids: this.selectedOrderIds,
                handyman_ids: handymanIds,
                date: moment(this.form.date).format('YYYY-MM-DD'),
                time: this.form.time,
                service_hours: this.form.service_hours?.id || 1
            };

            try {
                let response;
                if (this.isEditMode) {
                     response = await axios.post(`/api/admin/orders/bulk-assignment/${this.editingAssignmentId}/update`, payload);
                } else {
                     response = await axios.post('/api/admin/orders/bulk-assign', payload);
                }

                if (response.data.status === 'success') {
                    this.$notification('success', response.data.message);
                    this.$emit('refresh-orders'); 
                    
                    // Reset selection
                    this.cancelEdit(); // Helper to reset state
                    
                    // Close modal
                    $('#bulkOrderAssignModal').modal('hide');
                } else {
                    this.$notification('error', response.data.message || 'Failed to assign orders.');
                }
            } catch (error) {
                console.error(error);
                this.$notification('error', error.response?.data?.message || 'An error occurred.');
            } finally {
                this.isLoading = false;
            }
        },

        async fetchHistory() {
            this.isFetchingHistory = true;
            try {
                const params = {
                    date: this.historyDate ? moment(this.historyDate).format('YYYY-MM-DD') : null
                };
                const response = await axios.get('/api/admin/orders/bulk-assign-history', { params });
                this.historyList = response.data.data.data; // Paginated response usually in data.data
            } catch (error) {
                console.error("Error fetching history", error);
            } finally {
                this.isFetchingHistory = false;
            }
        },

        async editAssignment(item) {
             this.isLoading = true; // Show general loading
             try {
                 const response = await axios.get(`/api/admin/orders/bulk-assignment/${item.id}`);
                 if (response.data.status === 'success') {
                     const data = response.data.data;
                     
                     this.isEditMode = true;
                     this.editingAssignmentId = data.id;
                     
                     // 1. Set Form Data
                     this.form.date = new Date(data.date);
                     
                     // Parse Time (HH:mm:ss)
                     const [hours, minutes] = data.time.split(':');
                     this.form.time = { hours: parseInt(hours), minutes: parseInt(minutes) };
                     
                     // Find Service Hours Option
                     const sh = parseFloat(data.service_hours);
                     this.form.service_hours = this.serviceHourOptions.find(o => o.id === sh) || { id: sh, label: `${sh} Hrs` };
                     
                     // 2. Set Handymen
                     this.selectedHandymen = data.handymans;
                     
                     // 3. Set Orders
                     this.orderList = data.orders;
                     this.selectedOrderIds = this.orderList.map(o => o.id);
                     this.selectAllOrders = true;
                     
                     // 4. Switch Tab
                     this.activeTab = 'assign';
                 }
             } catch(error) {
                 console.error("Failed to load assignment details", error);
                 this.$notification('error', "Failed to load details");
             } finally {
                 this.isLoading = false;
             }
        },

        cancelEdit() {
            this.isEditMode = false;
            this.editingAssignmentId = null;
            this.selectedOrderIds = [];
            this.selectedHandymen = [];
            this.orderList = [];
            this.form.date = new Date();
            this.form.time = { hours: 8, minutes: 0 };
            
            // Re-fetch default list
            this.fetchPendingOrders();
        }
    }
};
</script>

<style scoped>
/* Adjust table responsiveness */
.table-responsive {
    scrollbar-width: thin;
}
:deep(.multiselect__content-wrapper) {
    z-index: 2050 !important;
}

.custom-thead th {
    background-color: #e2e8f0 !important;
    border: 1px solid #dee2e6;
    position: sticky;
    top: -1px;
    white-space: nowrap; 
}

.table-responsive table {
    margin-top: -1px;
}
</style>
